// Blocking "verify your email" wall. Shown in place of the app whenever the
// server reports `must_verify_email` (the launch flag is on and this account is
// unverified). Through the soft/tester phase `must_verify_email` is always false,
// so the dismissible VerifyEmailBanner is what users see instead.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { resendVerification } from "@/api/auth";
import { Button, Spinner } from "@/components/ui";

export function VerifyEmailWall() {
  const { t } = useTranslation();
  const { user, refreshMe, logout } = useAuth();
  const [resend, setResend] = useState<"idle" | "sending" | "sent">("idle");
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);

  const onResend = async () => {
    setResend("sending");
    try {
      await resendVerification();
      setResend("sent");
    } catch {
      setResend("idle");
    }
  };

  const onRecheck = async () => {
    setChecking(true);
    setCheckFailed(false);
    await refreshMe(); // re-reads /auth/me; clears the wall if now verified
    setChecking(false);
    // If still walled, refreshMe leaves must_verify_email true and this stays mounted.
    setCheckFailed(true);
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100 text-3xl">
        ✉️
      </div>
      <h1 className="text-2xl font-bold tracking-tight">{t("Verify your email")}</h1>
      <p className="mt-2 text-sm text-ink-faint">
        {user?.email
          ? t("We sent a verification link to {{email}}. Tap it to confirm your account, then come back here.", {
              email: user.email,
            })
          : t("We sent you a verification link. Tap it to confirm your account, then come back here.")}
      </p>

      <Button size="lg" className="mt-8 w-full" onClick={onRecheck} disabled={checking}>
        {checking ? (
          <span className="flex items-center justify-center gap-2">
            <Spinner className="h-4 w-4" /> {t("Checking…")}
          </span>
        ) : (
          t("I've verified — continue")
        )}
      </Button>

      <div className="mt-4 text-sm">
        {resend === "sent" ? (
          <span className="font-semibold text-green-700">{t("Link sent.")}</span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={resend === "sending"}
            className="font-semibold text-brand-600 underline underline-offset-2 disabled:opacity-50"
          >
            {resend === "sending" ? t("Sending…") : t("Resend link")}
          </button>
        )}
      </div>

      {checkFailed && (
        <p className="mt-3 text-xs text-red-600">
          {t("Still not verified. Tap the link in your email, then try again.")}
        </p>
      )}

      <button
        type="button"
        onClick={() => void logout()}
        className="mt-10 text-xs text-ink-faint underline underline-offset-2 hover:text-ink"
      >
        {t("Sign out")}
      </button>
    </div>
  );
}
